/**
 * deliverableConverters.js
 *
 * DashboardDeliverableManagementPageから抽出された純粋関数群
 * 成果物管理固有のデータ変換・領域チェック列処理を提供
 */

// 領域チェック列を actor_checks 配列からフラット化するキー名を生成
export function actorCheckKey(actorName) {
  return `actor_check__${actorName}`;
}

// 編集対象の座標を返す。
// 1つの成果物は1つの座標に属する前提だが、Notion 同期は複数座標に紐付いた成果物を
// 作り得る。その場合は先頭の座標を編集対象とし、残りの座標のリンクには触らない。
export function editingCoordinate(row) {
  return (row?.linked_coordinates || [])[0] || null;
}

// 座標をカスケード選択の照合に使える形に整える。
// 保存時に「読み込み時の座標」を組み立て直すために使う。
function toCoordinateRow(oemRegion, coordinate) {
  return {
    oemRegion: oemRegion || '',
    categoryName: coordinate?.category_name || '',
    largeCategoryName: coordinate?.large_category_name || '',
    middleCategoryName: coordinate?.middle_category_name || '',
    smallCategoryName: coordinate?.small_category || '',
    phaseName: coordinate?.phase_name || '',
  };
}

// APIレスポンスをテーブル行にフラット化
//
// カスケード列には編集対象の座標（linked_coordinates の先頭）を入れる。
// API の categoryName などは複数座標をカンマ結合した表示用の値で、これを
// 選択値として使うとどの座標とも一致せず、領域チェックが選べなくなる。
// 一覧の表示と絞り込みは linked_coordinates から組み立てる。
export function flattenRow(row) {
  const coordinates = Array.isArray(row.linked_coordinates) ? row.linked_coordinates : [];
  const editing = coordinates[0] || null;
  const flat = {
    id: row.id,
    oemRegion: row.oemRegion || '',
    ...toCoordinateRow(row.oemRegion, editing),
    product: row.product || '',
    deliverableUrl: row.deliverableUrl || '',
    noDeliverable: Boolean(row.noDeliverable),
    createdBy: row.createdBy || '',
    createdAt: row.createdAt || '',
    updatedBy: row.updatedBy || '',
    updatedAt: row.updatedAt || '',
    updated_at_iso: row.updated_at_iso || null,
    link_version: row.link_version ?? null,
    // 一覧の表示・絞り込みと、保存時の「移動元」の特定に使う。
    // 編集でカスケード列を変えてもここは読み込み時のまま保つ。
    linked_coordinates: coordinates,
    // 座標の変更を「移動」にするには変更前の OEM×対地 も要る。
    // oemRegion 列は編集で書き換わるため、読み込み時の値を別に持つ。
    loadedOemRegion: row.oemRegion || '',
  };

  // actor_checks を個別列に展開（チェックボックス用にboolean）
  if (Array.isArray(row.actor_checks)) {
    row.actor_checks.forEach((ac) => {
      flat[actorCheckKey(ac.actor_name)] = Boolean(ac.checked);
    });
  }

  return flat;
}

// 領域チェック列を抽出する。
// 対象の領域と表示順はバックエンド (DELIVERABLE_ACTOR_NAMES) が決めており、
// 全行が同じ領域を同じ順で返すため、先頭行の順序をそのまま使う。
export function extractActorCheckColumns(rows) {
  if (!rows.length) return [];
  const first = rows[0];
  if (!Array.isArray(first.actor_checks)) return [];
  return first.actor_checks.map((ac) => ({
    actorName: ac.actor_name,
    actorId: ac.actor_id,
    key: actorCheckKey(ac.actor_name),
  }));
}

// 紐付く座標から、指定した階層の値を重複なく取り出す。
// バックエンドの表示用カンマ結合と同じ「重複を除いて並べる」を配列で行う。
export function distinctCoordinateValues(row, targetKey) {
  const values = [];
  (row?.linked_coordinates || []).forEach((coordinate) => {
    const value = coordinate?.[targetKey] || '';
    if (value && !values.includes(value)) values.push(value);
  });
  return values;
}

// 進捗管理へのリレーションを決めるカスケード選択の階層。
// 上から順に絞り込み、全階層が一致した進捗管理と成果物をリレーションする。
export const LINK_LEVELS = [
  { rowKey: 'oemRegion', targetKey: 'oem_region' },
  { rowKey: 'categoryName', targetKey: 'category_name' },
  { rowKey: 'largeCategoryName', targetKey: 'large_category_name' },
  { rowKey: 'middleCategoryName', targetKey: 'middle_category_name' },
  { rowKey: 'smallCategoryName', targetKey: 'small_category' },
  { rowKey: 'phaseName', targetKey: 'phase_name' },
];

// 上位 levelCount 階層が row の選択と一致する候補だけに絞る
export function filterLinkTargets(targets, row, levelCount) {
  return targets.filter((target) => LINK_LEVELS.slice(0, levelCount).every(
    (level) => (row[level.rowKey] || '') === (target[level.targetKey] || ''),
  ));
}

// 指定階層の選択肢。上位の選択で絞った結果に存在する値だけを返すので、
// 進捗管理に無い組み合わせは選択肢に現れない。
export function buildLinkOptions(targets, row, levelIndex) {
  const values = new Set(
    filterLinkTargets(targets, row, levelIndex)
      .map((target) => target[LINK_LEVELS[levelIndex].targetKey] || ''),
  );
  return Array.from(values).sort((a, b) => a.localeCompare(b, 'ja'));
}

// 上位を変更したときに、矛盾する下位の選択をクリアする
export function clearInvalidLinkSelections(targets, row, changedLevelIndex) {
  const next = { ...row };
  for (let index = changedLevelIndex + 1; index < LINK_LEVELS.length; index += 1) {
    const { rowKey } = LINK_LEVELS[index];
    if (!buildLinkOptions(targets, next, index).includes(next[rowKey] || '')) {
      next[rowKey] = '';
    }
  }
  return next;
}

// 選択中の座標に存在する進捗管理を、領域名でまとめて返す。
// ここに無い領域は、その小項目を持つ進捗管理が存在しないのでチェックできない。
export function buildSchedulesByActor(targets, row) {
  const byActor = new Map();
  filterLinkTargets(targets, row, LINK_LEVELS.length).forEach((target) => {
    (target.progress_schedules || []).forEach((schedule) => {
      const name = schedule.actor_name || '';
      if (!name) return;
      if (!byActor.has(name)) byActor.set(name, []);
      byActor.get(name).push(schedule.id);
    });
  });
  return byActor;
}

// チェックされている領域に対応する進捗管理の ID を返す
export function resolveProgressScheduleIds(targets, row) {
  const byActor = buildSchedulesByActor(targets, row);
  const ids = new Set();
  byActor.forEach((scheduleIds, actorName) => {
    if (row[actorCheckKey(actorName)]) scheduleIds.forEach((id) => ids.add(id));
  });
  return Array.from(ids);
}

// この保存で扱う範囲＝「選択中の座標」＋「読み込み時の座標」に属する進捗管理の ID。
//
// 読み込み時の座標を含めないと、カスケードで小項目などを選び直したときに
// 変更前のリンクが範囲外になって残り、移動ではなく追加になる。
// 一方で範囲を全リンクに広げると、同期などで別の座標にも紐付いていた場合に
// その分が巻き添えで消えるため、触る座標だけに絞る。
export function resolveScopeProgressScheduleIds(targets, row) {
  const ids = new Set();
  const collect = (scopeRow) => {
    buildSchedulesByActor(targets, scopeRow).forEach((scheduleIds) => {
      scheduleIds.forEach((id) => ids.add(id));
    });
  };

  collect(row);

  const loaded = editingCoordinate(row);
  if (loaded) collect(toCoordinateRow(row.loadedOemRegion || row.oemRegion, loaded));

  return Array.from(ids);
}

// 座標を変えたときに、選べなくなった領域のチェックを外す
export function clearUnavailableActorChecks(targets, row, actorNames) {
  const available = buildSchedulesByActor(targets, row);
  const next = { ...row };
  actorNames.forEach((name) => {
    if (!available.has(name)) next[actorCheckKey(name)] = false;
  });
  return next;
}

export function buildSelectOptions(rows, keys) {
  const optionsByKey = Object.fromEntries(keys.map((key) => [key, new Set()]));
  rows.forEach((row) => {
    keys.forEach((key) => {
      const value = row[key];
      if (value !== undefined && value !== null && value !== '') {
        optionsByKey[key].add(value);
      }
    });
  });
  return Object.fromEntries(
    Object.entries(optionsByKey).map(([key, values]) => [key, ['', ...Array.from(values)]]),
  );
}
